import React, { useState, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import {
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";
import { MoreVertical } from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import {
  APPLICATION_STATUS_CONFIG,
  APPLICATION_VALID_TRANSITIONS,
} from "./StatusControl/configs/applicationConfig";
import RejectionPopover from "./RejectionPopover";
import JustificationPopover from "./JustificationPopover";
import type { ApplicationStatus } from "../types/shared.types";

interface Application {
  id: number;
  candidate_id: number;
  candidate_first_name?: string;
  candidate_last_name?: string;
  first_name?: string;
  last_name?: string;
  candidate_email?: string;
  email?: string;
  main_skill?: string;
  status: string;
  ai_match_score?: number;
}

interface ApplicationRowProps {
  application: Application;
  onEmailClick: (candidateId: number) => void;
  onGenerateCVClick: (application: Application) => void;
}

function getMatchScoreStyle(score: number): {
  backgroundColor: string;
  color: string;
} {
  if (score >= 70) {
    return { backgroundColor: "#ecfdf5", color: "#059669" };
  }
  if (score >= 40) {
    return { backgroundColor: "#fffbeb", color: "#d97706" };
  }
  return { backgroundColor: "#fef2f2", color: "#dc2626" };
}

const ApplicationRow: React.FC<ApplicationRowProps> = ({
  application,
  onEmailClick,
  onGenerateCVClick,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [showRejection, setShowRejection] = useState(false);
  const [showJustification, setShowJustification] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteNote, setDeleteNote] = useState("");

  const statusConfig = APPLICATION_STATUS_CONFIG[application.status];
  const transitions = APPLICATION_VALID_TRANSITIONS[application.status] || [];
  const forwardTransitions = transitions.filter((s) => s !== "rejected");
  const primaryTransition = forwardTransitions[0] || null;
  const secondaryTransitions = forwardTransitions.slice(1);
  const canReject = transitions.includes("rejected");

  const name =
    `${application.candidate_first_name || application.first_name || ""} ${application.candidate_last_name || application.last_name || ""}`.trim();
  const email = application.candidate_email || application.email;

  const updateMutation = useMutation(
    async (payload: {
      status: ApplicationStatus;
      note?: string;
      override_justification?: string;
      rejection_reason?: string;
      rejection_reason_note?: string;
    }) => {
      if (payload.status === "rejected") {
        return recruitingAPI.rejectApplication(
          application.id,
          payload.rejection_reason || "other",
          {
            rejection_reason_note: payload.rejection_reason_note,
          },
        );
      }
      return recruitingAPI.updateApplicationStatus(
        application.id,
        payload.status,
        {
          note: payload.note,
          override_justification: payload.override_justification,
        },
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("applications");
        queryClient.invalidateQueries(["application", application.id]);
        resetDialogs();
      },
      onError: (error: Error) => {
        alert(`Failed to update status: ${error.message}`);
      },
    },
  );

  const deleteMutation = useMutation(
    async ({ reason, note }: { reason: string; note: string }) => {
      return recruitingAPI.deleteApplication(application.id, {
        reason: reason || undefined,
        note: note || undefined,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("applications");
        queryClient.invalidateQueries("job-applications");
        resetDialogs();
      },
      onError: (error: Error) => {
        alert(`Failed to delete application: ${error.message}`);
      },
    },
  );

  const resetDialogs = () => {
    setShowRejection(false);
    setShowJustification(false);
    setShowDeleteConfirm(false);
    setPendingStatus(null);
    setDeleteReason("");
    setDeleteNote("");
  };

  const handleTransition = (targetStatus: string) => {
    setMenuAnchor(null);
    if (targetStatus === "rejected") {
      setShowRejection(true);
      return;
    }
    if (
      application.status === "not_matched_ai" &&
      targetStatus === "approved"
    ) {
      setPendingStatus(targetStatus);
      setShowJustification(true);
      return;
    }
    updateMutation.mutate({
      status: targetStatus as ApplicationStatus,
      note: "Status changed via quick action",
    });
  };

  const handleReject = (reason: string, note?: string) => {
    updateMutation.mutate({
      status: "rejected" as ApplicationStatus,
      rejection_reason: reason,
      rejection_reason_note: note,
    });
  };

  const handleJustifiedTransition = (justification: string) => {
    if (!pendingStatus) return;
    updateMutation.mutate({
      status: pendingStatus as ApplicationStatus,
      override_justification: justification,
      note: `Override: ${justification}`,
    });
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        className="application-row"
        role="button"
        tabIndex={0}
        style={{ borderLeftColor: statusConfig?.color || "#6b7280" }}
        onClick={() => navigate(`/applications/${application.id}`)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(`/applications/${application.id}`);
          }
        }}
      >
        {/* Name */}
        <div
          style={{ flex: 2, fontWeight: 600, color: "#1f2937", minWidth: 0 }}
        >
          <Tooltip title={email || ""} placement="top" arrow>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {name || "Unknown"}
            </span>
          </Tooltip>
        </div>

        {/* Main Skill */}
        <div
          style={{
            flex: 1.5,
            color: "#6b7280",
            fontSize: "13px",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {application.main_skill || "\u2014"}
        </div>

        {/* AI Match */}
        <div style={{ width: 48, textAlign: "center", flexShrink: 0 }}>
          {application.ai_match_score != null ? (
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: "4px",
                ...getMatchScoreStyle(application.ai_match_score),
              }}
            >
              {application.ai_match_score}%
            </span>
          ) : (
            <span style={{ color: "#d1d5db", fontSize: "12px" }}>
              {"\u2014"}
            </span>
          )}
        </div>

        {/* Status Chip */}
        <div style={{ width: 130, flexShrink: 0 }}>
          <Chip
            label={statusConfig?.label || application.status}
            size="small"
            sx={{
              backgroundColor: statusConfig?.color || "#6b7280",
              color: "white",
              fontWeight: 500,
              fontSize: "11px",
            }}
          />
        </div>

        {/* Primary Action */}
        <div
          role="presentation"
          style={{ width: 110, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {primaryTransition && (
            <button
              className="btn-outline btn-sm"
              onClick={() => handleTransition(primaryTransition)}
              disabled={updateMutation.isLoading}
              style={{
                fontSize: "12px",
                padding: "4px 10px",
                whiteSpace: "nowrap",
              }}
            >
              {APPLICATION_STATUS_CONFIG[primaryTransition]?.label ||
                primaryTransition}
            </button>
          )}
        </div>

        {/* Overflow Menu */}
        <div
          role="presentation"
          style={{ width: 36, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={(e: MouseEvent<HTMLButtonElement>) =>
              setMenuAnchor(e.currentTarget)
            }
            aria-label="More actions"
          >
            <MoreVertical size={16} />
          </IconButton>
        </div>
      </div>

      {/* Overflow Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        {secondaryTransitions.map((status) => (
          <MenuItem key={status} onClick={() => handleTransition(status)} dense>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: APPLICATION_STATUS_CONFIG[status]?.color,
                marginRight: 8,
                flexShrink: 0,
              }}
            />
            {APPLICATION_STATUS_CONFIG[status]?.label || status}
          </MenuItem>
        ))}
        {canReject && (
          <MenuItem
            onClick={() => handleTransition("rejected")}
            dense
            sx={{ color: "#ef4444" }}
          >
            Reject
          </MenuItem>
        )}
        {(secondaryTransitions.length > 0 || canReject) && <Divider />}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            navigate(`/candidates/${application.candidate_id}`);
          }}
          dense
        >
          View Candidate
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onEmailClick(application.candidate_id);
          }}
          dense
        >
          Send Email
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onGenerateCVClick(application);
          }}
          dense
        >
          Generate CV
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setShowDeleteConfirm(true);
          }}
          dense
          sx={{ color: "#dc2626" }}
        >
          Delete Application
        </MenuItem>
      </Menu>

      {/* Rejection Popover */}
      <RejectionPopover
        show={showRejection}
        onClose={() => setShowRejection(false)}
        onReject={handleReject}
        isLoading={updateMutation.isLoading}
        align="right"
      />

      {/* Justification Dialog */}
      <JustificationPopover
        show={showJustification}
        onClose={() => {
          setShowJustification(false);
          setPendingStatus(null);
        }}
        onSubmit={handleJustifiedTransition}
        isLoading={updateMutation.isLoading}
        align="right"
      />

      {/* Delete Confirmation Popover */}
      {showDeleteConfirm && (
        <form
          role="presentation"
          onSubmit={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Escape") resetDialogs();
          }}
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            zIndex: 100,
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            minWidth: "260px",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              fontWeight: 600,
              marginBottom: "8px",
              color: "#991b1b",
            }}
          >
            Delete this application?
          </div>
          <select
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 8px",
              borderRadius: "4px",
              border: "1px solid #d1d5db",
              fontSize: "12px",
              marginBottom: "8px",
            }}
          >
            <option value="">Select a reason...</option>
            <option value="duplicate">Duplicate Application</option>
            <option value="wrong_job">Wrong Job</option>
            <option value="created_by_mistake">Created by Mistake</option>
            <option value="test_data">Test Data</option>
            <option value="other">Other (specify in notes)</option>
          </select>
          <textarea
            value={deleteNote}
            onChange={(e) => setDeleteNote(e.target.value)}
            placeholder="Optional note..."
            rows={2}
            style={{
              width: "100%",
              padding: "6px 8px",
              borderRadius: "4px",
              border: "1px solid #d1d5db",
              fontSize: "12px",
              marginBottom: "8px",
              resize: "vertical",
            }}
          />
          <div
            style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}
          >
            <button
              onClick={resetDialogs}
              style={{
                padding: "4px 10px",
                fontSize: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                backgroundColor: "white",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() =>
                deleteMutation.mutate({
                  reason: deleteReason,
                  note: deleteNote,
                })
              }
              disabled={!deleteReason || deleteMutation.isLoading}
              style={{
                padding: "4px 10px",
                fontSize: "12px",
                border: "none",
                borderRadius: "4px",
                backgroundColor: deleteReason ? "#dc2626" : "#d1d5db",
                color: "white",
                cursor: deleteReason ? "pointer" : "not-allowed",
                fontWeight: 500,
              }}
            >
              {deleteMutation.isLoading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ApplicationRow;
